import React from "react";
import Image from "next/image";
import DsButton from "@components/services/DsButton";
import HeroBadge from "@components/services/HeroBadge";
import { HeroData } from "@components/services/types";

interface ServiceHeroProps {
  hero: HeroData;
  /**
   * Replaces the photo inside the square card — e.g. a generated diagram
   * (guide chapters). When set, `hero.photoSrc` / `photoAlt` are not needed.
   */
  visual?: React.ReactNode;
}

export default function ServiceHero({ hero, visual }: ServiceHeroProps) {
  const highlightAfterTitle = hero.highlightPosition === "after";

  if (!visual && !(hero.photoSrc && hero.photoAlt)) {
    throw new Error("ServiceHero: pass `visual` or set hero.photoSrc and hero.photoAlt");
  }

  return (
    // id="hero" is the anchor every landing-page JSON declares in
    // `metadata.speakable`. Without it those selectors match nothing and the
    // hero — the passage answer engines are most likely to lift — is not
    // marked as speakable. Keep the id even if the styling hook changes.
    <section id="hero" className="service-hero">
      <div className="container service-hero-grid">
        <div>
          <HeroBadge text={hero.badge} />
          <h1 className="display service-hero-title">
            {hero.highlight ? (
              highlightAfterTitle ? (
                <>
                  {hero.title} <em>{hero.highlight}</em>
                </>
              ) : (
                <>
                  <em>{hero.highlight}</em> {hero.title}
                </>
              )
            ) : (
              hero.title
            )}
          </h1>
          <p className="lede service-hero-lede">{hero.description}</p>
          <div className="service-hero-ctas">
            <DsButton
              href={hero.primaryCta.href}
              label={hero.primaryCta.label}
              variant={hero.primaryCta.variant}
            />
            {hero.secondaryCta ? (
              <DsButton
                href={hero.secondaryCta.href}
                label={hero.secondaryCta.label}
                variant={hero.secondaryCta.variant}
                target={hero.secondaryCta.target}
                rel={hero.secondaryCta.rel}
              />
            ) : null}
          </div>
          {hero.leadMeta?.length ? (
            <div className="service-lead-meta">
              {hero.leadMeta.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          ) : null}
          {hero.heroFacts?.length ? (
            <dl className="service-hero-facts">
              {hero.heroFacts.map((fact) => (
                <div className="service-hero-fact" key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd>{fact.text}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {hero.claimsFootnote ? (
            <p className="service-hero-claims-footnote">{hero.claimsFootnote}</p>
          ) : null}
        </div>
        <div
          className={`service-hero-photo-card${visual ? " service-hero-photo-card--visual" : ""}`}
        >
          {visual ? (
            visual
          ) : (
            <Image
              src={hero.photoSrc as string}
              alt={hero.photoAlt as string}
              width={1200}
              height={1500}
              priority
              fetchPriority="high"
              sizes="(min-width: 1200px) 560px, (min-width: 768px) 50vw, 100vw"
              quality={70}
            />
          )}
          <div className="service-hero-tag">{hero.tag}</div>
        </div>
      </div>
    </section>
  );
}
